import { useState, type FormEvent } from "react";
import { ArrowRight, KeyRound, Leaf, LockKeyhole } from "lucide-react";
import { isSupabaseConfigured } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import { Button, Card, Field, InlineMessage } from "../components/ui";

export function LoginPage() {
  const { signIn, requestPasswordReset, authError } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [resetMode, setResetMode] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      if (resetMode) {
        await requestPasswordReset(email);
        setMessage(
          "If that email belongs to a team account, a password-reset link has been sent.",
        );
      } else {
        await signIn(email, password);
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not sign in.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login-page">
      <section className="login-brand-panel">
        <img src="/brand/house-care-logo.jpeg" alt="House Care Management, Refilwe Garden Services" />
        <div className="login-brand-copy">
          <span className="brand-chip"><Leaf size={16} /> Refilwe Garden Services</span>
          <h1>Daily work, clients and garden costs in one place.</h1>
          <p>Private access for the House Care Management team.</p>
        </div>
      </section>

      <section className="login-form-panel">
        <Card className="login-card">
          <div className="login-icon"><LockKeyhole size={25} /></div>
          <p className="eyebrow">House Care Management (Pty) Ltd</p>
          <h2>{resetMode ? "Reset your password" : "Team sign in"}</h2>
          <p className="login-intro">
            {resetMode
              ? "Enter the email used for your team account."
              : "Use the email and password provided by the company owner."}
          </p>

          {!isSupabaseConfigured ? (
            <InlineMessage>
              The app needs its Supabase URL and publishable key before login can work. Follow the setup guide included with the source code.
            </InlineMessage>
          ) : (
            <form onSubmit={submit} className="stack-form">
              <Field label="Email address" required>
                <input
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="name@example.com"
                  required
                />
              </Field>
              {!resetMode && (
                <Field label="Password" required>
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    minLength={8}
                    required
                  />
                </Field>
              )}
              {(message || authError) && (
                <InlineMessage tone={message.startsWith("If that") ? "success" : "error"}>
                  {message || authError}
                </InlineMessage>
              )}
              <Button type="submit" disabled={busy}>
                {busy ? "Please wait…" : resetMode ? "Send reset link" : "Sign in"}
                {!busy && <ArrowRight size={18} />}
              </Button>
            </form>
          )}

          {isSupabaseConfigured && (
            <button className="text-button" onClick={() => { setResetMode(!resetMode); setMessage(""); }}>
              <KeyRound size={16} />
              {resetMode ? "Back to sign in" : "Forgot password?"}
            </button>
          )}

          <p className="login-footer">Registration 2026/291023/07</p>
        </Card>
      </section>
    </main>
  );
}

export function UpdatePasswordPage() {
  const { updatePassword, signOut } = useAuth();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (password.length < 12) {
      setMessage("Use at least 12 characters for the new password.");
      return;
    }
    if (password !== confirmPassword) {
      setMessage("The two passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await updatePassword(password);
      setMessage("Password updated. You can continue to the app.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not update the password.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="single-card-page">
      <Card className="login-card">
        <div className="login-icon"><KeyRound size={25} /></div>
        <h1>Choose a new password</h1>
        <p className="login-intro">Use at least 12 characters and do not reuse an old password.</p>
        <form onSubmit={submit} className="stack-form">
          <Field label="New password" required>
            <input type="password" autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} required />
          </Field>
          <Field label="Confirm new password" required>
            <input type="password" autoComplete="new-password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} required />
          </Field>
          {message && <InlineMessage tone={message.startsWith("Password updated") ? "success" : "error"}>{message}</InlineMessage>}
          <Button type="submit" disabled={busy}>{busy ? "Updating…" : "Update password"}</Button>
          <Button type="button" variant="ghost" onClick={() => void signOut()}>Cancel and sign out</Button>
        </form>
      </Card>
    </main>
  );
}
